import { createAsyncThunk } from '@reduxjs/toolkit';
import { postOrder } from 'src/apis/order';
import { types as rs, Backoff } from 'earn-ts';
import { getFullCurrentOrder, getMenu, postItemsToOrder } from 'src/apis';
import { TUpdateSetPrice, updateSetPrices } from 'src/utils';
import { addTables, IOrderState, setMenuDef } from './orderReducer';
import { isEmpty } from 'lodash';

export enum ErrorCode {
  Expired = 'Expired',
}

function checkRequestResult(err: any) {
  // note, Error object is "serialized" by Redux which removes custom properties
  if (err?.responseBody == 'Expired') {
    return new Error(ErrorCode.Expired);
  }

  return null;
}

export const getMenuThunk = createAsyncThunk(
  'order/getMenu',
  async (id: null, api) => {
    const response = await getMenu(rootSessionSelector(api.getState()));
    return response.data;
  },
);

// maybe RTK Query later
const rootSessionSelector = (state: any) => state.order.session;

const updateAllSetPrices = (
  state: any,
  r: rs.OrderContainer,
  menuDef: rs.MenuDef | null,
) => {
  let orderStore = state.order as IOrderState;
  return updateSetPrices(
    orderStore.dataSelectedMenus,
    r,
    menuDef ?? orderStore.menuDef!,
  );
};

export const postOrderThunk = createAsyncThunk(
  'order/sendOrder',
  async (dataOrder: rs.Order, { rejectWithValue }) => {
    try {
      const response = await postOrder(dataOrder);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const currentOrderBackoff = new Backoff({ increment: 100, keep: 7000 });

// now back to our scheduled programming
const getCurrentOrderRepeater = (api: any) =>
  new Promise((resolve: (v: rs.OrderContainer) => void, reject) => {
    const bodyfn = () => {
      getFullCurrentOrder(rootSessionSelector(api.getState())).then(
        r => {
          currentOrderBackoff.reset();
          resolve(r);
        },
        err => {
          let r = checkRequestResult(err);
          if (r) {
            reject(r);
            return;
          } else {
            let giveup = !currentOrderBackoff.schedule(() => {
              bodyfn();
            });

            if (giveup) {
              reject(new Error('gave up'));
              return;
            }
          }
        },
      );
    };
    bodyfn();
  });

// get menu runs together with the getCurrentOrder thing but we need
// the menudef to be present asap so we dispatch a 'side' action
const getMenuAndSet = async (shopId: string, api: any) => {
  let menudef: rs.MenuDef = (await getMenu(rootSessionSelector(api.getState()))).data;
  api.dispatch(setMenuDef(menudef));
  api.dispatch(addTables(menudef.tables));
  return menudef;
};

type TStartupRequestsOpts = {
  shopId: string | null;
  withMenu: boolean;
};

// calculating price after getting current order depends on menudef to be
// present so we wait for both in this composite action
export const getCurrentOrderThunk = createAsyncThunk(
  'order/getCurrentOrderThunk',
  async (
    { shopId, withMenu }: TStartupRequestsOpts,
    api,
  ): Promise<[rs.MenuDef | null, rs.OrderContainer, TUpdateSetPrice]> => {
    let [menuDef, fullOrder] = await Promise.all([
      withMenu ? getMenuAndSet(shopId!, api) : Promise.resolve(null),
      getCurrentOrderRepeater(api),
    ]);
    let prices = await updateAllSetPrices(api.getState(), fullOrder, menuDef);

    return [menuDef, fullOrder, prices];
  },
);

// .-----------------------------------------------------------.
const postItemsBackoff = new Backoff({ max: 9, increment: 150 });

export const postItemsToOrderThunk = createAsyncThunk(
  'order/postItemsToOrder',
  (sets: rs.OrderMenuSet[], api) =>
    new Promise((resolve: (v: {}) => void, reject) => {
      const bodyfn = () => {
        postItemsToOrder(rootSessionSelector(api.getState()), sets).then(
          r => {
            postItemsBackoff.reset();

            if (!isEmpty(r.data.errors) && r.data.errors.length) {
              return reject(new Error(r.data.errors));
            }
            // need to be able to see current order
            api.dispatch(
              getCurrentOrderThunk({ shopId: null, withMenu: false }),
            );
            resolve(r.data);
          },
          err => {
            let r = checkRequestResult(err);
            if (r) {
              reject(r);
            } else {
              let giveup = !postItemsBackoff.schedule(() => {
                bodyfn();
              });
              if (giveup) reject(new Error('gave up'));
            }
          },
        );
      };

      bodyfn();
    }),
);
